'use client';

import { useEffect, useState } from 'react';
import { INITIAL_SSR_HITS } from 'helpers/algolia/plp-dataset-core';

/**
 * Tiles shown per page (pagination) / per "Load More" click / per infinite-scroll batch.
 *
 * Same number as the SSR batch, and deliberately the same CONSTANT: the server ships exactly one
 * tile page of products in the HTML, so first paint contains what the grid shows and nothing more.
 * Changing one without the other would either ship dead records or leave the first page short.
 */
export const TILE_PAGE_SIZE = INITIAL_SSR_HITS;

export type TileLoadingStyle = 'all' | 'pagination' | 'loadMore' | 'infinite';

/**
 * Drives the CMS `product_tiles_loading_style` for the SSR grid. Only the first TILE_PAGE_SIZE tiles
 * are server-rendered; the rest arrive with the dataset top-up (see local-store) and this decides
 * which of the loaded ones are visible:
 *   - 'infinite'   → first TILE_PAGE_SIZE, then +TILE_PAGE_SIZE auto-revealed as you scroll (default)
 *   - 'loadMore'   → first TILE_PAGE_SIZE, then +TILE_PAGE_SIZE per "Load More" click (cumulative)
 *   - 'pagination' → one page of TILE_PAGE_SIZE at a time
 *   - 'all'        → everything loaded is visible
 *
 * `resetToken` — a string identifying the current query (refinements + sort). The view resets to the
 * first page whenever it changes. It is NOT keyed on the result count, which used to serve the same
 * purpose: the count now also changes when the top-up lands, and resetting on that would yank a
 * visitor who had already clicked "Load More" or paged forward back to page 1 mid-browse.
 *
 * `isHidden(index)` returns true for tiles that should be `hidden` (kept in the DOM, removed from view).
 */
export function useTileLoading(total: number, style: TileLoadingStyle, resetToken = '') {
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(TILE_PAGE_SIZE);

  // Reset when the query changes (e.g. a facet is toggled, or the sort order changes).
  useEffect(() => {
    setPage(1);
    setVisibleCount(TILE_PAGE_SIZE);
  }, [resetToken]);

  const totalPages = Math.max(1, Math.ceil(total / TILE_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const isHidden = (index: number): boolean => {
    if (style === 'pagination') {
      return index < (currentPage - 1) * TILE_PAGE_SIZE || index >= currentPage * TILE_PAGE_SIZE;
    }
    if (style === 'loadMore' || style === 'infinite') {
      return index >= visibleCount;
    }
    return false;
  };

  const loadMore = () => setVisibleCount((count) => Math.min(count + TILE_PAGE_SIZE, total));

  return { totalPages, currentPage, setPage, visibleCount, loadMore, isHidden };
}
